import type { OpenCollaborationsSortColumn } from "@unbox/api";
import { BookOpen01, Plus, SearchMd } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { useOpenCollaborationsCount, useOpenCollaborationsList } from "@/api/collaborations";
import { usePagination } from "@/api/pagination";
import { useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { useCache } from "@/hooks/use-cache";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useSort } from "@/hooks/use-sort";
import { CollabsListEmptyState } from "@/pages/collaborations/collabs-list-empty-state";
import { OpenCollaborationsTable } from "../collaborations/open-collaborations-table";

export const OpenGroupsPage = () => {
  const [search, setSearch] = useState("");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);
  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<OpenCollaborationsSortColumn>("productTitle");
  const { data: openData, isLoading: isLoadingOpen } = useOpenCollaborationsList({
    search: debouncedSearch,
    pagination: pagination.pagination,
    sort: sort ? { column: sort.column, direction: sort.direction } : undefined,
  });
  const { data: openCount } = useOpenCollaborationsCount(debouncedSearch);
  const cachedOpenCount = useCache(openCount);
  const loadedOpenTotal = useCache(openData?.total);
  const { data: shopStatus } = useTikTokShopConnectionStatus();

  // A user parked on a high page can search into a smaller result set and see an empty
  // page even though matching rows exist earlier — reset pagination whenever the search changes.
  useEffect(() => {
    pagination.setPage(1);
  }, [debouncedSearch]);

  const handleResetOpenFilters = () => {
    setSearch("");
    clearSearch();
    pagination.setPage(1);
  };

  // Genuine zero-data — or no Shop Access — replaces the whole page with the
  // full-bleed first-run empty state. A search keeps the inline table state.
  const shopUnauthorized = shopStatus ? !shopStatus.connected : false;
  if (!debouncedSearch && (shopUnauthorized || (!isLoadingOpen && (openData?.total ?? 0) === 0))) {
    return <CollabsListEmptyState variant="open" />;
  }

  if (!debouncedSearch && isLoadingOpen && loadedOpenTotal === undefined) return <LoadingPageState />;

  return (
    <PageLayout
      title="Open Collaborations"
      subtitle="Open to every TikTok Shop affiliate. You set the commission rate and creators opt in themselves."
      icon={BookOpen01}
      callToAction={
        <Tooltip title="Open Collaborations cannot currently be created in Unbox.">
          <TooltipTrigger>
            <span>
              <Button color="primary" size="sm" iconLeading={<Plus className="size-4" />} isDisabled>
                Create New Collaboration
              </Button>
            </span>
          </TooltipTrigger>
        </Tooltip>
      }
    >
      <div className="flex shrink-0 flex-row items-center justify-end">
        <div className="flex items-center flex-row gap-2">
          <span className="text-sm font-medium text-tertiary mr-2">
            {isLoadingOpen ? "Loading..." : `${cachedOpenCount ?? 0} results`}
          </span>
          <Input
            size="sm"
            inputClassName="text-sm"
            className="w-72"
            value={search}
            onChange={setSearch}
            placeholder="Search Collaboration or Creator name"
            icon={SearchMd}
          />
        </div>
      </div>
      <OpenCollaborationsTable
        rows={isLoadingOpen ? undefined : (openData?.collaborations ?? [])}
        sorting={{ descriptor, onSortChange: setFromDescriptor }}
        pagination={{
          page: pagination.page,
          totalPages: Math.ceil((openData?.total ?? 0) / pagination.pagination.limit),
          onPageChange: pagination.setPage,
        }}
        hasActiveFilters={Boolean(debouncedSearch)}
        onClearFilters={handleResetOpenFilters}
      />
    </PageLayout>
  );
};
